import { useState } from "react";
import { Camera } from "lucide-react";

const PHOTOS = [
  {
    id: "bts-1",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/27zre83e_440B3609-61B5-47B9-98CF-EF2E3AB9AF12.png",
    alt: "Behind the scenes — interview setup with multi-cam coverage",
    caption: "Interview Set · Multi-Cam",
  },
  {
    id: "bts-2",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/mmq847vz_IMG_5934.jpg",
    alt: "Behind the scenes — podcast set with lighting",
    caption: "Podcast Set",
  },
  {
    id: "bts-3",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/88itt0rv_IMG_5969.jpg",
    alt: "Behind the scenes — recording in progress",
    caption: "Rolling",
  },
  {
    id: "bts-4",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/5q5qaace_IMG_5971.jpg",
    alt: "Behind the scenes — music session capture",
    caption: "Music Session",
  },
  {
    id: "bts-5",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/5odbhibw_IMG_5989.jpg",
    alt: "Behind the scenes — close-up capture in library",
    caption: "Close-Up",
  },
  {
    id: "bts-6",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/ecayi6jx_IMG_6497.jpg",
    alt: "Behind the scenes — event venue lighting setup",
    caption: "Event Setup",
  },
  {
    id: "bts-7",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/x88lyhtf_IMG_6303.jpg",
    alt: "Behind the scenes — podcast interview with two hosts",
    caption: "Podcast Interview",
  },
  {
    id: "bts-8",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/w6645gwn_IMG_6304.jpg",
    alt: "Behind the scenes — live switcher and remote podcast setup",
    caption: "Live Switching",
  },
  {
    id: "bts-9",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/g5pe7yw9_IMG_6315.jpg",
    alt: "Behind the scenes — lifestyle product shoot in lobby",
    caption: "Studio Setup",
  },
  {
    id: "bts-10",
    src: "https://customer-assets.emergentagent.com/job_cinematic-minimal-1/artifacts/0djeqd9a_IMG_5994.jpg",
    alt: "Behind the scenes — interview shoot with grand piano",
    caption: "Interview Shoot",
  },
];

function Photo({ p, className = "" }) {
  const [failed, setFailed] = useState(false);
  return (
    <figure
      data-testid={`bts-photo-${p.id}`}
      className={`group relative overflow-hidden bg-[#0a0a0a] border border-[#1a1a1a] ${className}`}
    >
      {!failed ? (
        <img
          src={p.src}
          alt={p.alt}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-[#1a0000] via-[#0a0a0a] to-[#000] flex flex-col items-center justify-center text-white/40 p-6">
          <Camera size={32} strokeWidth={1.2} className="text-[#E60000] mb-3" />
          <p className="eyebrow text-center text-white/60">{p.caption}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/30 mt-2 text-center">
            Photo
          </p>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
        <p className="eyebrow text-white/80">{p.caption}</p>
      </div>
    </figure>
  );
}

export default function BTS() {
  return (
    <section
      id="bts"
      data-testid="bts-section"
      className="relative py-24 md:py-36 px-6 md:px-12 lg:px-16 border-t border-[#1a1a1a] bg-[#070707]"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16 reveal">
        <div>
          <p className="eyebrow text-[#E60000] mb-5">/ Behind the Scenes</p>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">
            On set
            <br />
            <span className="text-outline">with us.</span>
          </h2>
        </div>
        <p className="md:max-w-md text-white/70 leading-relaxed md:text-lg">
          The craft happens off-camera too. A look at the rigs, sets and quiet
          focus behind the films you've just watched.
        </p>
      </div>

      {/* Featured hero photo + grid below */}
      <div className="space-y-3 md:space-y-5 reveal">
        {/* Hero photo - full width on the JPG that renders everywhere */}
        <Photo
          p={PHOTOS[0]}
          className="w-full h-[280px] md:h-[520px]"
        />

        {/* Remaining photos in a responsive 4-column grid */}
        <div className="hidden md:grid grid-cols-4 gap-3 md:gap-5">
          {PHOTOS.slice(1).map((p) => (
            <Photo key={p.id} p={p} className="aspect-[4/5]" />
          ))}
        </div>

        {/* Mobile: horizontal scroll strip */}
        <div className="md:hidden -mx-6 px-6 flex gap-3 overflow-x-auto no-scrollbar">
          {PHOTOS.slice(1).map((p) => (
            <div key={p.id} className="shrink-0 w-[60vw] h-[220px]">
              <Photo p={p} className="h-full w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
